import dayjs from 'dayjs';
import { usePersistedDataStore } from '../components/store';
import { toast } from './toast';
import logger from '../utils/logger';

/**
 * Parse a date string to a Date object, handling various formats
 * @param {string} dateString - Date string to parse
 * @returns {Date|null} Parsed date or null if invalid
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  
  // Handle MM/DD/YYYY format
  if (typeof dateString === 'string' && dateString.includes('/')) {
    const [month, day, year] = dateString.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  
  // Handle YYYY-MM-DD format
  if (typeof dateString === 'string' && dateString.includes('-')) {
    return new Date(dateString);
  }
  
  // Handle Date objects
  if (dateString instanceof Date) {
    return dateString;
  }
  
  // Try parsing as is
  const parsed = new Date(dateString);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Format a date string to MM/DD/YYYY without timezone issues
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string or 'Never' if invalid
 */
export const formatDateWithoutTimezone = (dateString) => {
  if (!dateString) return 'Never';
  
  // Handle YYYY-MM-DD format without timezone conversion
  if (typeof dateString === 'string' && dateString.includes('-')) {
    const [year, month, day] = dateString.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return 'Never';
    
    // Create date in local timezone to avoid timezone shifts
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US');
  }
  
  // Handle other formats by trying to parse normally
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Never';
    return date.toLocaleDateString('en-US');
  } catch (error) {
    return 'Never';
  }
};

/** Calendar day only: `YYYY-MM-DD` (ISO prefix, M/D/Y, or Date-parsed e.g. RFC `... GMT`). */
const isUtcCourtDateString = (s) =>
  /\b(GMT|UTC)\b/i.test(s) ||
  /T\d{2}:\d{2}:\d{2}(\.\d{3})?Z\b/i.test(s) ||
  /^[A-Za-z]{3},\s\d{1,2}\s[A-Za-z]{3}\s/.test(s);

/** Court-date filter value for OA cases that have no hearing date. */
export const UNASSIGNED_COURT_DATE = 'unassigned';

export const isUnassignedCourtDateCase = (row) => {
  const action = String(row?.SCARDeterminationAction ?? '').trim();
  const hearing = row?.SCARHearingDate;
  return action === 'OA' && (hearing == null || String(hearing).trim() === '');
};

export const parseCourtDateCalendarKey = (value) => {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:\b|T|$)/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\b/);
  if (slash) {
    let y = Number(slash[3]);
    if (String(slash[3]).length === 2) y += y >= 70 ? 1900 : 2000;
    const mo = String(Number(slash[1])).padStart(2, '0');
    const d = String(Number(slash[2])).padStart(2, '0');
    return `${y}-${mo}-${d}`;
  }
  const t = Date.parse(s);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  const useUtc = isUtcCourtDateString(s);
  const y = useUtc ? d.getUTCFullYear() : d.getFullYear();
  const m = useUtc ? d.getUTCMonth() + 1 : d.getMonth() + 1;
  const day = useUtc ? d.getUTCDate() : d.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/** Local Date for a calendar day. Date-only strings stay on that day instead of shifting to the previous evening. */
export const parseCalendarDate = (value) => {
  const key = parseCourtDateCalendarKey(value);
  if (!key) return null;
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Display calendar day as `M/D/YYYY` (same calendar key as {@link parseCourtDateCalendarKey}). */
export const formatCourtDateDisplayDay = (value) => {
  const key = parseCourtDateCalendarKey(value);
  if (!key) return '';
  const [y, mo, d] = key.split('-');
  return `${Number(mo)}/${Number(d)}/${y}`;
};

/** Wall-clock time as stored (no timezone conversion). Court datetimes are naive local. */
const parseCourtDateClockParts = (value) => {
  if (value == null || value === '') return null;
  const clock = String(value).trim().match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!clock) return null;
  return { hours: Number(clock[1]), minutes: Number(clock[2]), seconds: Number(clock[3] || 0) };
};

/** Comparable timestamp for sorting: `YYYYMMDDHHmmss` (number), or null. */
export const parseCourtDateSortValue = (value) => {
  const key = parseCourtDateCalendarKey(value);
  if (!key) return null;
  const [y, mo, d] = key.split('-').map(Number);
  const clock = parseCourtDateClockParts(value);
  const hours = clock?.hours ?? 0;
  const minutes = clock?.minutes ?? 0;
  const seconds = clock?.seconds ?? 0;
  return y * 1e10 + mo * 1e8 + d * 1e6 + hours * 1e4 + minutes * 100 + seconds;
};

/** Display as `M/D/YYYY at 9:30am`. Time omitted if the value has no clock time. */
export const formatCourtDateDisplayDateTime = (value) => {
  const day = formatCourtDateDisplayDay(value);
  if (!day) return '';
  const clock = parseCourtDateClockParts(value);
  if (!clock) return day;
  const h12 = clock.hours % 12 || 12;
  const mm = String(clock.minutes).padStart(2, '0');
  return `${day} at ${h12}:${mm}${clock.hours >= 12 ? 'pm' : 'am'}`;
};

/**
 * Unified function to calculate date range based on evaluation date and presets
 * This function ensures dates are always exactly on month boundaries (1t of the month)
 * to prevent off-by-one errors when calculating month-based ranges
 * @param {string} evalDate - Evaluation date in YYYY-MM-DD format
 * @param {string} beforePreset - Before preset (e.g., 12m)
 * @param {string} afterPreset - After preset (e.g.,12or 'nolimit')
 * @returns {Array} [startDate, endDate] in YYYY-MM-DD format
 */
export const calculateUnifiedDateRange = (evalDate, beforePreset, afterPreset) => {
  if (!evalDate) {
    const currentYear = dayjs().year();
    const fallbackEvalDate = dayjs(`${currentYear - 1}-07-01`);
    return calculateUnifiedDateRange(fallbackEvalDate.format('YYYY-MM-DD'), beforePreset, afterPreset);
  }
  
  const evalDateObj = dayjs(evalDate);
  if (!evalDateObj.isValid()) {
    const currentYear = dayjs().year();
    const fallbackEvalDate = dayjs(`${currentYear - 1}-07-01`);
    return calculateUnifiedDateRange(fallbackEvalDate.format('YYYY-MM-DD'), beforePreset, afterPreset);
  }
  
  // Parse presets
  const beforeMonths = parseInt(beforePreset?.replace('m', '') || '12');
  const afterMonths = afterPreset === 'nolimit' ? null : parseInt(afterPreset?.replace('m', '') || '12');
  
  // Calculate dates ensuring they land exactly on month boundaries (1st of the month)
  let startDate = null;
  let endDate = null;
  
  if (beforeMonths) {
    // For before" dates, we want to go back X months from the evaluation date
    // and ensure we land on the 1 of that month
    const startDateObj = evalDateObj.subtract(beforeMonths, 'month').startOf('month');
    startDate = startDateObj.format('YYYY-MM-DD');
  }
  
  if (afterMonths) {
    // Forafter" dates, we want to go forward X months from the evaluation date
    // and ensure we land on the 1 of that month
    const endDateObj = evalDateObj.add(afterMonths, 'month').startOf('month');
    endDate = endDateObj.format('YYYY-MM-DD');
  }
  
  return [startDate, endDate];
};

/**
 * Get evaluation date for a given municipality and tax year.
 * Also fetches RAR/EQ and writes them to persisted store (used only by EQWarningDialog in SummaryCaseReviewTable).
 * @param {string} muniCode - Municipality code
 * @param {number} taxYear - Tax year
 * @returns {Promise<string>} Evaluation date in YYYY-MM-DD format
 */
export const getEvaluationDate = async (muniCode, taxYear) => {
  const { setRar, setEq } = usePersistedDataStore.getState();
  try {
    // Import axiosInstance dynamically to avoid circular dependencies
    const { default: axiosInstance } = await import('../fetchConfig/axiosConfig');
    
    // logger.log('Fetching evaluation date for MuniCode:', muniCode, 'TaxYear:', taxYear);
    
    if (!taxYear) {
      // Default to July 1st of the year prior to current calendar year
      const currentYear = dayjs().year();
      const fallbackDate = dayjs(`${currentYear - 1}-07-01`);
      // logger.log('Using fallback date (no taxYear):', fallbackDate.format('YYYY-MM-DD'));
      return fallbackDate.format('YYYY-MM-DD');
    }

    if (!muniCode || muniCode === 'All' || muniCode.length === 1) {
      // in this scenario, you should pass back nulls for both the eval date and the date range.
      const fallbackDate = dayjs(`${taxYear - 1}-07-01`);
      // logger.log('No Municode using taxyear assuming 7/1:', fallbackDate.format('YYYY-MM-DD'));
      return fallbackDate.format('YYYY-MM-DD');
    }
    
    const response = await axiosInstance.post('/get_eval_date', {
      MuniCode: muniCode,
      TaxYear: taxYear
    });

    // RAR/EQ for EQ warning dialog only (persisted store)
    try {
      const rarAndEq = await axiosInstance.post('/get_rar_and_eq', {
        MuniCode: muniCode,
        TaxYear: taxYear
      });

      if (rarAndEq.data && rarAndEq.data.success) {
        // Set RAR and EQ values -- this is semi-legacy logic. we updated the rest of the repo to no longer use these states, but for the EQ vs. RAR check for 
        // An individually run area, this will still be used.
        setRar(rarAndEq.data.rar);
        setEq(rarAndEq.data.eq);

        if (rarAndEq.data.use_projected_rar && rarAndEq.data.message) {
          toast.info(rarAndEq.data.message, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } else {
        setRar(null);
        setEq(null);
      }
    } catch (rarEqError) {
      logger.error('[getEvaluationDate] Error fetching RAR and EQ values:', rarEqError);
    }

    if (response.data && response.data.success && response.data.eval_date) {
      // logger.log('Received evaluation date from API:', response.data.eval_date);
      return response.data.eval_date;
    } else {
      // Default to July 1st of the year prior to tax year
      const fallbackDate = dayjs(`${taxYear - 1}-07-01`);
      // logger.log('Using fallback date (API returned no data):', fallbackDate.format('YYYY-MM-DD'));
      return fallbackDate.format('YYYY-MM-DD');
    }
  } catch (error) {
    logger.error('[getEvaluationDate] Error fetching eval date:', error);
    // Default to July 1st of the year prior to tax year
    const fallbackDate = taxYear ? dayjs(`${taxYear - 1}-07-01`) : dayjs(`${dayjs().year() - 1}-07-01`);
    logger.log('Using fallback date (API error):', fallbackDate.format('YYYY-MM-DD'));
    return fallbackDate.format('YYYY-MM-DD');
  }
};

/**
 * Check if a sale date is outside the specified date range
 * @param {string} saleDate - Sale date string in YYYY-MM-DD format
 * @param {string} earliestCutoffDate - Start date string in YYYY-MM-DD format
 * @param {string} latestCutoffDate - End date string in YYYY-MM-DD format
 * @returns {boolean} True if outside date range, false if within range
 */
export const isOutsideDateRange = (saleDate, earliestCutoffDate, latestCutoffDate) => {
  if (!saleDate || !earliestCutoffDate || !latestCutoffDate) {
    return false;
  }

  const saleDateObj = parseLocalDate(saleDate);
  const earliestDate = parseLocalDate(earliestCutoffDate);
  const latestDate = parseLocalDate(latestCutoffDate);

  return saleDateObj < earliestDate || saleDateObj > latestDate;
};

/**
 * Calculate distance between two points in miles
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number|null} Distance in miles or null if coordinates are missing
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) {
    logger.log('Distance Calculation - Missing Coordinates:', { lat1, lon1, lat2, lon2 });
    return null;
  }

  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return Number(distance.toFixed(2)); // Return distance in miles with 2 decimal places
};


/**
 * Comprehensive function to calculate and set date range based on various inputs
 * This function handles all the complex logic for converting between presets, filter data, and date ranges
 * @param {Object} options - Configuration options
 * @param {string} options.evalDate - Evaluation date in YYYY-MM-DD format
 * @param {string} options.dateRangeType - 'preset' or 'custom'
 * @param {string} options.beforePreset - Before preset (e.g., '12m')
 * @param {string} options.afterPreset - After preset (e.g., '12m' or 'nolimit')
 * @param {number} options.earliestSaleDate - Days before evaluation date (from filter)
 * @param {number} options.latestSaleDate - Days after evaluation date (from filter)
 * @param {Array} options.currentDateRange - Current date range [startDate, endDate]
 * @returns {Object} Object containing calculated date range and metadata
 */
export const calculateDateRangeFromSettings = (options) => {
  const {
    evalDate,
    dateRangeType = 'preset',
    beforePreset = '12m',
    afterPreset = '12m',
    earliestSaleDate = null,
    latestSaleDate = null,
    currentDateRange = [null, null]
  } = options;

  // Ensure we have a valid evaluation date
  const evalDateObj = dayjs(evalDate);
  if (!evalDateObj.isValid()) {
    const currentYear = dayjs().year();
    const fallbackEvalDate = dayjs(`${currentYear - 1}-07-01`);
    return calculateDateRangeFromSettings({
      ...options,
      evalDate: fallbackEvalDate.format('YYYY-MM-DD')
    });
  }

  let calculatedDateRange = [null, null];
  let calculatedDateRangeType = dateRangeType;
  let calculatedBeforePreset = beforePreset;
  let calculatedAfterPreset = afterPreset;
  let presetPatterns = [
    { days: 180, preset: '6m' },
    { days: 365, preset: '12m' },
    { days: 547, preset: '18m' },
    { days: 730, preset: '24m' }
  ];

  // If we have filter data (earliestSaleDate/latestSaleDate), use that to calculate dates
  if (earliestSaleDate !== null || latestSaleDate !== null) {
    
    // Calculate start date (earliest sale date relative to eval date)
    let startDate = null;
    if (earliestSaleDate) {
      // Check if this matches a preset pattern
      const beforeMatch = presetPatterns.find(pattern => 
        Math.abs(pattern.days - earliestSaleDate) <= 5
      );
      if (beforeMatch) {
        // Use months instead of days for preset patterns
        const months = parseInt(beforeMatch.preset.replace('m', ''));
        startDate = evalDateObj.subtract(months, 'month').startOf('month').format('YYYY-MM-DD');
      } else {
        // Use days for custom values
        startDate = evalDateObj.subtract(earliestSaleDate, 'day').format('YYYY-MM-DD');
      }
    }
    
    // Calculate end date (latest sale date relative to eval date)
    let endDate = null;
    if (latestSaleDate) {
      // Check if this matches a preset pattern
      const afterMatch = presetPatterns.find(pattern => 
        Math.abs(pattern.days - latestSaleDate) <= 5
      );
      if (afterMatch) {
        // Use months instead of days for preset patterns
        const months = parseInt(afterMatch.preset.replace('m', ''));
        endDate = evalDateObj.add(months, 'month').startOf('month').format('YYYY-MM-DD');
      } else {
        // Use days for custom values
        endDate = evalDateObj.add(latestSaleDate, 'day').format('YYYY-MM-DD');
      }
    }
    
    calculatedDateRange = [startDate, endDate];

    // Check if the days match preset patterns
    const beforeMatchesPreset = earliestSaleDate && 
      presetPatterns.some(pattern => Math.abs(pattern.days - earliestSaleDate) <= 5);
    
    const afterMatchesPreset = latestSaleDate && 
      presetPatterns.some(pattern => Math.abs(pattern.days - latestSaleDate) <= 5);
    
    const afterIsNoLimit = latestSaleDate === null;
    
    if (beforeMatchesPreset && (afterMatchesPreset || afterIsNoLimit)) {
      // Use preset mode - convert days back to preset values
      calculatedDateRangeType = 'preset';
      
      // Find matching preset for earliest days (before evaluation date)
      if (earliestSaleDate) {
        const beforeMatch = presetPatterns.find(pattern => 
          Math.abs(pattern.days - earliestSaleDate) <= 5
        );
        if (beforeMatch) {
          calculatedBeforePreset = beforeMatch.preset;
        }
      }
      
      // Find matching preset for latest days (after evaluation date)
      if (latestSaleDate) {
        const afterMatch = presetPatterns.find(pattern => 
          Math.abs(pattern.days - latestSaleDate) <= 5
        );
        if (afterMatch) {
          calculatedAfterPreset = afterMatch.preset;
        }
      } else {
        calculatedAfterPreset = 'nolimit';
      }
    } else {
      // Use custom mode - dates don't match presets
      calculatedDateRangeType = 'custom';
    }
  } else if (dateRangeType === 'preset') {
    // Use preset mode with current presets
    calculatedDateRange = calculateUnifiedDateRange(evalDate, beforePreset, afterPreset);
  } else {
    // Use custom mode with current date range
    calculatedDateRange = currentDateRange;
  }

  const result = {
    dateRange: calculatedDateRange,
    dateRangeType: calculatedDateRangeType,
    beforePreset: calculatedBeforePreset,
    afterPreset: calculatedAfterPreset,
    earliestSaleDate,
    latestSaleDate
  };

  return result;
};

/**
 * Convert days back to preset values (helper function)
 * @param {number} earliestDays - Days before evaluation date
 * @param {number} latestDays - Days after evaluation date
 * @returns {Object} Object with beforePreset and afterPreset
 */
export const convertDaysToPreset = (earliestDays, latestDays) => {
  const presetPatterns = [
    { days: 180, preset: '6m' },
    { days: 365, preset: '12m' },
    { days: 547, preset: '18m' },
    { days: 730, preset: '24m' }
  ];
  
  let beforePreset = '12m'; // default
  let afterPreset = '12m'; // default
  
  // Find matching preset for earliest days (before evaluation date)
  if (earliestDays) {
    const beforeMatch = presetPatterns.find(pattern => 
      Math.abs(pattern.days - earliestDays) <= 5 // Allow 5 days tolerance
    );
    if (beforeMatch) {
      beforePreset = beforeMatch.preset;
    }
  }
  
  // Find matching preset for latest days (after evaluation date)
  if (latestDays) {
    const afterMatch = presetPatterns.find(pattern => 
      Math.abs(pattern.days - latestDays) <= 5 // Allow 5 days tolerance
    );
    if (afterMatch) {
      afterPreset = afterMatch.preset;
    }
  } else {
    afterPreset = 'nolimit'; // No limit on end date
  }
  
  return { beforePreset, afterPreset };
};

/**
 * Decode integer day values to preset values and calculate actual date range
 * @param {number} earliestSaleDate - Days before evaluation date (from filter)
 * @param {number} latestSaleDate - Days after evaluation date (from filter)
 * @param {string} evalDate - Evaluation date in YYYY-MM-DD format
 * @returns {Object} Object with dateRangeType, beforePreset, afterPreset, and calculated dateRange
 */
export const decodeFilterDateSettings = (earliestSaleDate, latestSaleDate, evalDate) => {
  // Define standard preset patterns
  const presetPatterns = [
    { days: 180, preset: '6m' },
    { days: 365, preset: '12m' },
    { days: 547, preset: '18m' },
    { days: 730, preset: '24m' }
  ];
  
  let dateRangeType = 'preset'; // Default to preset
  let beforePreset = '12m'; // Default preset
  let afterPreset = '12m'; // Default preset
  let dateRange = [null, null];
  
  // Check if we have date range data
  if (
    (earliestSaleDate !== null && earliestSaleDate !== undefined) ||
    (latestSaleDate !== null && latestSaleDate !== undefined)
  ) {
    
    // Check if earliest sale date matches a preset pattern
    let earliestMatchesPreset = false;
    if (earliestSaleDate !== null && earliestSaleDate !== undefined) {
      earliestMatchesPreset = presetPatterns.some(pattern => 
        Math.abs(pattern.days - earliestSaleDate) <= 5 // Allow 5 days tolerance
      );
      if (earliestMatchesPreset) {
        const match = presetPatterns.find(pattern => 
          Math.abs(pattern.days - earliestSaleDate) <= 5
        );
        beforePreset = match.preset;
      }
    }
    
    // Check if latest sale date matches a preset pattern
    let latestMatchesPreset = false;
    if (latestSaleDate !== null && latestSaleDate !== undefined) {
      latestMatchesPreset = presetPatterns.some(pattern => 
        Math.abs(pattern.days - latestSaleDate) <= 5 // Allow 5 days tolerance
      );
      if (latestMatchesPreset) {
        const match = presetPatterns.find(pattern => 
          Math.abs(pattern.days - latestSaleDate) <= 5
        );
        afterPreset = match.preset;
      }
    } else {
      // If no latest sale date, it means "no limit"
      afterPreset = 'nolimit';
      latestMatchesPreset = true; // Treat "no limit" as matching preset
    }
    
    // If both dates match preset patterns, use preset mode
    // If either date doesn't match, use custom mode
    if (earliestMatchesPreset && latestMatchesPreset) {
      dateRangeType = 'preset';
      // Calculate the actual date range using the evaluation date and presets
      if (evalDate) {
        dateRange = calculateUnifiedDateRange(evalDate, beforePreset, afterPreset);
      }
    } else {
      dateRangeType = 'custom';
      // For custom mode, calculate dates based on the actual day values
      if (evalDate) {
        const evalDateObj = dayjs(evalDate);
        if (evalDateObj.isValid()) {
          let startDate = null;
          let endDate = null;
          
          if (earliestSaleDate) {
            startDate = evalDateObj.subtract(earliestSaleDate, 'day').format('YYYY-MM-DD');
          }
          
          if (latestSaleDate) {
            endDate = evalDateObj.add(latestSaleDate, 'day').format('YYYY-MM-DD');
          }
          
          dateRange = [startDate, endDate];
        }
      }
    }
  }
  
  return {
    dateRangeType,
    beforePreset,
    afterPreset,
    dateRange
  };
};

/**
 * Process filter data and convert to settings format
 * @param {Object} filterData - Filter data from backend
 * @param {string} evalDate - Evaluation date
 * @returns {Object} Processed settings object
 */
export const processFilterDataToSettings = (filterData, evalDate) => {
  if (!filterData) return null;

  // Use the helper function to decode date settings
  const dateSettings = decodeFilterDateSettings(
    filterData.EarliestSaleDate,
    filterData.LatestSaleDate,
    evalDate
  );

  const settings = {
    squareFeetFilter: filterData.SqftPctCutoff !== null && filterData.SqftPctCutoff !== undefined ? filterData.SqftPctCutoff : 40,
    acreageFilter: filterData.AcresPctCutoff !== null && filterData.AcresPctCutoff !== undefined ? filterData.AcresPctCutoff : 40,
    grossAdjPctFilter: filterData.AdjPctCutoff !== null && filterData.AdjPctCutoff !== undefined ? filterData.AdjPctCutoff : 40,
    netAdjPctFilter: filterData.NetAdjPctCutoff !== null && filterData.NetAdjPctCutoff !== undefined ? filterData.NetAdjPctCutoff : 30,
    // Set toggle states - if filter value is null/0, disable it, otherwise enable it
    squareFeetFilterEnabled: filterData.SqftPctCutoff !== null && filterData.SqftPctCutoff !== 0,
    acreageFilterEnabled: filterData.AcresPctCutoff !== null && filterData.AcresPctCutoff !== 0,
    grossAdjPctFilterEnabled: filterData.AdjPctCutoff !== null && filterData.AdjPctCutoff !== 0,
    netAdjPctFilterEnabled: filterData.NetAdjPctCutoff !== null && filterData.NetAdjPctCutoff !== 0,
    compFilters: [
      ...(filterData.MLSOnly ? ['mlsOnly'] : []),
      ...(filterData.SaleConditionIssue ? ['saleConditionIssue'] : []),
      ...(filterData.UseTimeAdjustments ? ['useTimeAdjustments'] : [])
    ],
    earliestSaleDate: filterData.EarliestSaleDate,
    latestSaleDate: filterData.LatestSaleDate
  };

  // Return settings with the decoded date information
  return {
    ...settings,
    dateRange: dateSettings.dateRange,
    dateRangeType: dateSettings.dateRangeType,
    beforePreset: dateSettings.beforePreset,
    afterPreset: dateSettings.afterPreset
  };
}; 